import { globalConfirm } from '../components/ConfirmDialog';
import { AddressLookup, PostcodeLookup } from '../components/AddressLookup';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader, DataTableToolbar, PaginationBar, ActiveBadge, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import InteractiveTable from '../components/InteractiveTable';
import api, { getApiErrorMessage } from '../services/api';
import ModuleReports from './Reports/ModuleReports';
import { INVOICE_POINTS_REPORTS } from './Reports/reportConfigs';
import { BarChart2, FileText as FileTextIcon } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, Download } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import DataFilesAnalytics from './DataFilesAnalytics/DataFilesAnalytics';
import { invoicePointsConfig } from './DataFilesAnalytics/configs';
import { LayoutDashboard as AnalyticsIcon } from 'lucide-react';

const EMPTY = { code: '', name: '', address: '', city: '', state_province: '', zip_postal: '', country: 'United States', telephone: '', fax: '', email: '', contact_name: '', active: true };
const COUNTRIES = ['United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'UAE', 'India', 'Singapore', 'Japan', 'Other'];

export default function InvoicePointsPage() {
  const [records, setRecords] = useState([]);
  const { canWrite: _canWrite } = usePermissions();
  const canWriteData = _canWrite('invoice_point');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewTab, setViewTab] = useState('analytics');
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortState, setSortState] = useState({ field: 'code', dir: 'asc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/api/data/invoice-points', { params: { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir } }); setRecords(res.data.data || []); setTotal(res.data.total); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load'); }
    setLoading(false);
  }, [search, page, pageSize, sortState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.put(`/api/data/invoice-points/${form.code}`, form);
      else await api.post('/api/data/invoice-points', form);
      toast.success(isEdit ? 'Updated' : 'Created'); setModalOpen(false); load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const deleteRecord = async () => {
    if (!await globalConfirm('Delete?')) return;
    try { await api.delete(`/api/data/invoice-points/${form.code}`); toast.success('Deleted'); setModalOpen(false); load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Delete failed')); }
  };


  const toggleFav = async (r) => {
    const { toggleFavorite } = await import('../utils/favorites');
    const result = await toggleFavorite('invoice-points', r.code);
    if (result !== null) {
      setRecords(prev => {
        const updated = prev.map(rec => (rec === r) ? { ...rec, is_favorite: result } : rec);
        const starred = updated.filter(rec => rec.is_favorite);
        const unstarred = updated.filter(rec => !rec.is_favorite);
        return [...starred, ...unstarred];
      });
    }
  };

  const defaultCols = useMemo(() => [
    { key: 'code', label: 'Invoice Point Code', width: 160, sortField: 'code', visible: true },
    { key: 'name', label: 'Name', width: 220, sortField: 'name', visible: true },
    { key: 'city', label: 'City', width: 140, sortField: 'city', visible: true },
    { key: 'country', label: 'Country', width: 140, sortField: 'country', visible: true },
    { key: 'telephone', label: 'Telephone', width: 140, visible: true },
    { key: 'email', label: 'Email', width: 200, visible: true },
    { key: 'contact_name', label: 'Contact', width: 160, visible: true },
    { key: 'active', label: 'Active', width: 80, sortField: 'active', visible: true },
    { key: 'address', label: 'Address', width: 220, visible: true, addressCol: true },
    { key: 'state_province', label: 'State / Province', width: 140, visible: true, addressCol: true },
    { key: 'zip_postal', label: 'Zip / Postal', width: 110, visible: true, addressCol: true },
  ], []);

  const renderCell = useCallback((col, r) => {
    switch (col.key) {
      case 'code': return <span className="font-mono font-semibold text-zinc-900 text-sm">{r.code}</span>;
      case 'name': return <span className="font-medium text-sm">{r.name}</span>;
      case 'active': return <ActiveBadge active={r.active} />;
      default: return <span className="text-sm">{r[col.key] != null ? String(r[col.key]) : '—'}</span>;
    }
  }, []);

  return (
    <AppShell>
      <PageHeader title="Invoice Point" description={`Total records: ${total}`} helpGuide="data-files"
        actions={viewTab === 'list' ? <>
          <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" /> Export</Button>
          <Button size="sm" onClick={() => { setForm(EMPTY); setIsEdit(false); setModalOpen(true); }} data-testid="invoice-point-add-button"><Plus className="h-4 w-4 mr-1" /> Add New</Button>
        </> : null}
      />
      <div className="flex gap-4 border-b mb-4" data-testid="invoice-points-view-tabs">
        <button onClick={() => setViewTab('analytics')} data-testid="invoice-points-tab-analytics"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'analytics' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <AnalyticsIcon className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="invoice-points-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <FileTextIcon className="h-4 w-4 inline mr-1.5" /> Invoice Points
        </button>
        <button onClick={() => setViewTab('reports')} data-testid="invoice-points-tab-reports"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'reports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="h-4 w-4 inline mr-1.5" /> Reports
        </button>
      </div>
      {viewTab === 'analytics' ? (<DataFilesAnalytics {...invoicePointsConfig} onCardClick={() => setViewTab('list')} />) : viewTab === 'reports' ? (
        <ModuleReports reports={INVOICE_POINTS_REPORTS} apiPrefix="/api/module-reports/data" module="Invoice Points" />
      ) : (<>
      <DataTableToolbar searchValue={search} onSearch={v => { setSearch(v); setPage(1); }} onClear={() => { setSearch(''); setPage(1); }} searchPlaceholder="Search invoice points..." toolbarSlotId="invoice-point-listing-toolbar" />
      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="py-12"><LoadingSpinner /></div> : records.length === 0 ? <EmptyState title="No invoice points" action={<Button size="sm" onClick={() => { setForm(EMPTY); setIsEdit(false); setModalOpen(true); }}>Add</Button>} /> : (
          <InteractiveTable
            tableId="invoice-point-listing"
            toolbarPortalId="invoice-point-listing-toolbar"
            defaultCols={defaultCols}
            data={records}
            renderCell={renderCell}
            onRowClick={(row) => { setForm({ ...row }); setIsEdit(true); setModalOpen(true); }}
            sortState={sortState}
            onSort={handleSort}
            rowKey="code"
          />
        )}
        <div className="px-4"><PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} /></div>
      </div>
      </>)}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center justify-between">Invoice Point - {isEdit ? 'Edit' : 'Add'}<div className="flex items-center gap-2 mr-6"><span className="text-sm font-normal text-muted-foreground">Active</span><Switch checked={form.active} onCheckedChange={v => f('active', v)} /></div></DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Invoice Point Code *</Label><Input value={form.code} onChange={e => f('code', e.target.value)} disabled={isEdit} /></div>
              <div className="space-y-1.5"><Label>Invoice Point Name *</Label><Input value={form.name} onChange={e => f('name', e.target.value)} /></div>
              <div className="col-span-2 space-y-2 border rounded-xl p-3 bg-muted/10">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold text-xs">Address Lookup</Label>
                  <span className="text-[10px] text-muted-foreground">Auto-fills fields below · US &amp; UK</span>
                </div>
                <AddressLookup placeholder="Type address to auto-fill…"
                  onSelect={({ address, city, state_province, zip_postal, country }) => {
                    if (address)       f('address', address);
                    if (city)          f('city', city);
                    if (state_province)f('state_province', state_province);
                    if (zip_postal)    f('zip_postal', zip_postal);
                    if (country)       f('country', country);
                  }} />
              </div>
              <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={e => f('address', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={e => f('city', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>State / Province</Label><Input value={form.state_province} onChange={e => f('state_province', e.target.value)} /></div>
              <div className="space-y-1.5">
                <Label>Zip / Postal Code</Label>
                <Input value={form.zip_postal} onChange={e => f('zip_postal', e.target.value)} />
                {form.country === 'United Kingdom' && (
                  <div className="mt-1.5"><PostcodeLookup onFound={({ zip_postal, city, state_province }) => {
                    f('zip_postal', zip_postal);
                    if (city)           f('city', city);
                    if (state_province) f('state_province', state_province);
                  }} /></div>
                )}
              </div>
              <div className="space-y-1.5"><Label>Country</Label><select value={form.country} onChange={e => f('country', e.target.value)} className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm">{COUNTRIES.map(c => <option key={c}>{c}</option>)}</select></div>
              <div className="space-y-1.5"><Label>Telephone</Label><Input value={form.telephone} onChange={e => f('telephone', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Fax</Label><Input value={form.fax} onChange={e => f('fax', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={e => f('email', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => f('contact_name', e.target.value)} /></div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            {isEdit && <Button variant="destructive" onClick={deleteRecord}>Delete</Button>}
            <Button onClick={save} disabled={!canWriteData} disabled={saving || !form.code || !form.name}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
